import { Matrix, Vector3 } from '@babylonjs/core'
import { useEffect, useRef } from 'react'
import { SceneManager } from '../../../../visualization/sceneManager'
import { Box, Typography } from '@mui/material'
import { LineMeasurement as Measurement } from '../../../../models/Measurement'
import { Line } from '@babylonjs/gui'

interface LineMeasurementProps {
  measurement: Measurement
  isVisible: boolean
  units?: 'FT'
}

const LineMeasurement = ({
  measurement,
  isVisible,
  units = 'FT',
}: LineMeasurementProps) => {
  const boxRef = useRef<HTMLDivElement>(null)
  const { startPoint, endPoint } = measurement

  useEffect(() => {
    const scene = SceneManager.getInstance().getScene()
    const engine = SceneManager.getInstance().getEngine()
    const camera = SceneManager.getInstance().getCamera()
    if (!scene || !camera || !engine) return

    const measurement3Dposition = new Vector3()
    const measurement2Dposition = new Vector3()
    const updateMeasurementPositionCallback = () => {
      const box = boxRef.current
      if (!box) {
        return
      }

      measurement3Dposition.set(
        (startPoint.x + endPoint.x) / 2,
        (startPoint.y + endPoint.y) / 2,
        (startPoint.z + endPoint.z) / 2,
      )

      Vector3.ProjectToRef(
        measurement3Dposition,
        Matrix.IdentityReadOnly,
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(
          engine.getRenderWidth(true),
          engine.getRenderHeight(true),
        ),
        measurement2Dposition,
      )

      const appbarHeight =
        document.getElementsByTagName('header').item(0)?.offsetHeight ?? 52
      const boxWidth = box.offsetWidth

      box.style.left = `${measurement2Dposition.x - boxWidth / 2}px`
      box.style.top = `${measurement2Dposition.y + appbarHeight}px`

      const measurementsUI = SceneManager.getInstance().measurementsUI
      const lineControl = measurementsUI?.getDescendants(
        true,
        (control): control is Line =>
          control instanceof Line &&
          control.name === 'LineMeasurement' &&
          control.metadata.id === measurement.id,
      )[0] as Line | undefined

      if (lineControl) {
        measurement3Dposition.copyFromFloats(
          startPoint.x,
          startPoint.y,
          startPoint.z,
        )

        Vector3.ProjectToRef(
          measurement3Dposition,
          Matrix.IdentityReadOnly,
          scene.getTransformMatrix(),
          camera.viewport.toGlobal(
            engine.getRenderWidth(true),
            engine.getRenderHeight(true),
          ),
          measurement2Dposition,
        )

        lineControl.x1 = measurement2Dposition.x
        lineControl.y1 = measurement2Dposition.y

        measurement3Dposition.copyFromFloats(endPoint.x, endPoint.y, endPoint.z)

        Vector3.ProjectToRef(
          measurement3Dposition,
          Matrix.IdentityReadOnly,
          scene.getTransformMatrix(),
          camera.viewport.toGlobal(
            engine.getRenderWidth(true),
            engine.getRenderHeight(true),
          ),
          measurement2Dposition,
        )

        lineControl.x2 = measurement2Dposition.x
        lineControl.y2 = measurement2Dposition.y
      }
    }

    updateMeasurementPositionCallback()

    const viewMatrixChangedObserver = camera.onViewMatrixChangedObservable.add(
      updateMeasurementPositionCallback,
    )

    const projectionMatrixChangedObserver =
      camera.onProjectionMatrixChangedObservable.add(
        updateMeasurementPositionCallback,
      )

    const onResizeObserver = engine.onResizeObservable.add(
      updateMeasurementPositionCallback,
    )

    return () => {
      viewMatrixChangedObserver.remove()
      projectionMatrixChangedObserver.remove()
      onResizeObserver.remove()
    }
  }, [startPoint, endPoint])

  return (
    <Box
      ref={boxRef}
      sx={{
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.47)',
        opacity: isVisible ? 0.9 : 0,
        background: 'rgba(4, 0, 46, 0.90)',
        backgroundBlendMode: 'multiply',
        boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(2px)',
        width: 'fit-content',
        height: 'fit-content',
        pointerEvents: 'all',
        position: 'fixed',
        display: isVisible ? 'flex' : 'none',
        flexDirection: 'row',
        alignItems: 'center',
        maxWidth: '128px',
      }}
    >
      <Box
        sx={{
          padding: '4px',
          display: 'flex',
          gap: '4px',
          '& .MuiTypography-root': {
            color: '#F9F9F9',
            fontFamily: 'Inter',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          },
        }}
      >
        <Typography>{measurement.length.toFixed(2)}</Typography>

        <Typography>{units}</Typography>
      </Box>
    </Box>
  )
}

export default LineMeasurement
